/**
 * Crisis-resource engagement tracking (OI-CDS-04).
 *
 * The alerts API rows carry two fields describing whether the patient
 * tapped any of the crisis resources shown for a suicide-risk alert:
 *
 *   crisisResourceTriggered: true | false | absent
 *   crisisResourceEngagements: [{ resourceKey, timestamp }]
 *
 * Three states, not two. `true` — tapped. `false` — the resources were
 * shown and not tapped. Absent — the API sends null for an alert whose
 * assessment never showed them, and null is dropped from the JSON, so
 * the field is simply missing. That last one is not an answer to "did
 * they tap it" and must not be read as a "No".
 *
 * Wording is deliberate: a tap only proves the link was TRIGGERED —
 * never render "Called" / "Contacted".
 */

export const CRISIS_RESOURCE_LABELS = {
  call_911: '911',
  lifeline_988: '988 Lifeline',
  crisis_text_line: 'Crisis Text Line',
};

export const getCrisisResourceLabel = resourceKey =>
  CRISIS_RESOURCE_LABELS[resourceKey] || resourceKey || '';

/**
 * Per-row composer for the Triggered column cell value. Collapses the
 * two API fields into one `{ triggered, engagements }` object because
 * the table cell renderer receives only its own column's value.
 *
 * `triggered` is passed through as the three-state it is, never
 * coerced: `Boolean(false)` and `Boolean(undefined)` are the same
 * value, and the column then showed a dash for every "No" the API
 * returned — which the "No" filter made visible, since it returns
 * nothing but rows whose cells all read as not applicable.
 *
 * Call it while mapping the rows (see AlertsTable) — never as a
 * convertDataFromApi `additionalKeys` callback keyed after an API
 * field: that helper spreads the callbacks over the row, so such a key
 * replaces the API value with this function before it ever runs.
 */
export const readCrisisTriggered = value => (value === true || value === false ? value : null);

export const buildCrisisTriggeredCellValue = alert => ({
  triggered: readCrisisTriggered(alert?.crisisResourceTriggered),
  engagements: alert?.crisisResourceEngagements || [],
});
